import { TIDBConnection, TIDBIndex, TIDBTable } from '@aksia/infrastructure';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class IndexedDBService {
  //#region Properties

  private indexedDB?: IDBFactory;

  readonly databases = new Map<string, IDBDatabase>();

  //#endregion

  constructor() {
    if (typeof window === 'undefined') {
      // Running in SSR/test environment, leave indexedDB undefined and
      // fail gracefully when methods are called.
      this.indexedDB = undefined;
      return;
    }

    this.indexedDB =
      (window as any).indexedDB ||
      (window as any).mozIndexedDB ||
      (window as any).webkitIndexedDB ||
      (window as any).msIndexedDB;

    if (!this.indexedDB) {
      throw new Error('IndexedDB is not supported in this browser.');
    }
  }

  //#region Create

  async openDatabase(options: TIDBConnection): Promise<boolean> {
    if (!options) {
      return Promise.reject();
    }
    if (!this.indexedDB)
      return Promise.reject(new Error('IndexedDB not available'));
    const command = this._openDatabase(options.name, options.version!);
    return command;
  }

  private _openDatabase(dbName: string, version: number): Promise<boolean> {
    if (!this.indexedDB)
      return Promise.reject(new Error('IndexedDB not available'));

    return new Promise((resolve, reject) => {
      // Some browsers may not implement indexedDB.databases(); guard it
      const databasesFn = (this.indexedDB as any).databases;
      if (typeof databasesFn !== 'function') {
        // Fallback: try opening to detect existence
        const request = this.indexedDB!.open(dbName, version);
        request.onsuccess = (event: Event) => {
          let db = (event.target as IDBOpenDBRequest).result;
          this.databases.set(dbName, db);
          resolve(true);
        };
        request.onerror = (event: Event) => reject(event);
        return;
      }

      (this.indexedDB as any)
        .databases()
        .then((databases: IDBDatabaseInfo[]) => {
          let dbExists = databases.some(
            (db) => db.name === dbName && db.version === version,
          );
          if (dbExists) {
            const request = this.indexedDB!.open(dbName, version);
            request.onsuccess = (event: Event) => {
              let db = (event.target as IDBOpenDBRequest).result;
              this.databases.set(dbName, db);
              resolve(true);
            };

            request.onerror = (event: Event) => {
              reject(event);
            };
          } else {
            resolve(false);
          }
        })
        .catch((err: any) => reject(err));
    });
  }

  async createDatabase(options: TIDBConnection): Promise<boolean> {
    if (!options) {
      return Promise.reject();
    }
    if (!this.indexedDB)
      return Promise.reject(new Error('IndexedDB not available'));
    const command = this._createDatabase(
      options.name,
      options.version!,
      options.tables,
    );

    return command;
  }

  private _createDatabase(
    dbName: string,
    version: number,
    tables: Array<TIDBTable> = [],
  ): Promise<boolean> {
    if (!this.indexedDB)
      return Promise.reject(new Error('IndexedDB not available'));

    return new Promise((resolve, reject) => {
      const request = this.indexedDB!.open(dbName, version);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        let db = (event.target as IDBOpenDBRequest).result;
        this.databases.set(dbName, db);
        tables?.forEach((table) => {
          if (!db?.objectStoreNames.contains(table.name)) {
            this._createTable(
              dbName,
              table.name,
              table.keyPath!,
              table.indices,
              table.autoIncrement,
            );
          }
        });
      };

      request.onsuccess = (event: Event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        this.databases.set(dbName, db);
        resolve(true);
      };

      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  private _createTable(
    dbName: string,
    tableName: string,
    keyPath: Array<string> | string,
    indices?: Array<TIDBIndex>,
    autoIncrement: boolean = false,
  ) {
    if (!this.databases.has(dbName)) {
      return;
    }

    const objectStore = this.databases
      .get(dbName)!
      .createObjectStore(tableName, {
        keyPath: keyPath,
        autoIncrement,
      });

    if (objectStore && indices) {
      indices?.forEach((index) => {
        if (!objectStore.indexNames.contains(index.name)) {
          objectStore.createIndex(index.name, index.keyPath!, {
            unique: index.unique || false,
            multiEntry: index.multiEntry || false,
          });
        }
      });
    }

    objectStore.transaction.oncomplete = () => {};
  }

  async insert(
    dbName: string,
    tableName: string,
    data: any,
    preClean?: { index: string },
  ): Promise<string> {
    if (preClean?.index) {
      await this.deleteByIndex(dbName, tableName, preClean.index);
    }
    const result = await this._insert(dbName, tableName, data);
    return result as string;
  }

  private _insert(dbName: string, tableName: string, data: any) {
    return new Promise((resolve, reject) => {
      Array.isArray(data) || (data = [data]);
      const tx = this.databases
        .get(dbName)!
        .transaction(tableName, 'readwrite');
      let table = tx.objectStore(tableName);
      let requests: Array<IDBRequest<IDBValidKey>> = [];

      data.forEach((item: any) => {
        if (!item.isDeleted && !item.markedForDeletion) {
          requests.push(table.put(item));
        } else {
          this._delete(dbName, tableName, item);
        }
      });

      tx.oncomplete = () => {
        data?.forEach((item: any, index: number) => {
          if (requests?.at(index)) {
            let request = requests[index];
            if (
              (request.source as IDBObjectStore)?.autoIncrement &&
              request.result
            ) {
              item[(request.source as IDBObjectStore)?.keyPath as string] =
                request.result;
            }
          }
        });
        resolve(data);
      };

      tx.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  //#endregion

  //#region Read

  async listTables(dbName: string): Promise<string[] | undefined> {
    const tables = await this._listTables(dbName);
    return tables;
  }

  private _listTables(dbName: string): Promise<string[] | undefined> {
    return new Promise((resolve) => {
      const tables = Array.from(this.databases.get(dbName)!.objectStoreNames);
      resolve(tables as string[]);
    });
  }

  async listAllIds(
    dbName: string,
    tableName: string,
  ): Promise<Array<string> | undefined> {
    const ids = await this._getAll(dbName, tableName)?.then((data) => {
      return data?.map((item: any) => item.id);
    });
    return ids;
  }

  async getAll(
    dbName: string,
    tableName: string,
    filter?: { index: string; value: Array<string | number> },
  ): Promise<Array<unknown> | undefined> {
    const data = await this._getAll(dbName, tableName, filter);
    return data;
  }

  private _getAll(
    dbName: string,
    tableName: string,
    filter?: { index: string; value: Array<string | number> },
  ): Promise<Array<unknown> | undefined> {
    if (!filter) {
      return new Promise((resolve, reject) => {
        const request = this.databases
          .get(dbName)!
          .transaction(tableName)
          .objectStore(tableName)
          .getAll();

        request.onsuccess = () => {
          const result = request.result;
          resolve(result);
        };

        request.onerror = (err) => {
          reject(err);
        };
      });
    } else {
      return new Promise((resolve, reject) => {
        const request = this.databases
          .get(dbName)!
          .transaction(tableName)
          .objectStore(tableName)
          .index(filter.index)
          .getAll(filter.value);

        request.onsuccess = () => {
          const result = request.result;
          resolve(result);
        };

        request.onerror = (err) => {
          reject(err);
        };
      });
    }
  }

  async get(
    dbName: string,
    tableName: string,
    id: number | string | Array<number | string>,
  ): Promise<unknown | undefined> {
    if (Array.isArray(id)) {
      const results = await Promise.all(
        id.map((singleId) => this._get(dbName, tableName, singleId)),
      );
      return results;
    } else {
      const data = await this._get(dbName, tableName, id);
      return data;
    }
  }

  private _get(
    dbName: string,
    tableName: string,
    id: number | string,
  ): Promise<unknown | undefined> {
    return new Promise((resolve, reject) => {
      const request = this.databases
        .get(dbName)!
        .transaction(tableName)
        .objectStore(tableName)
        .get(id);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result);
      };

      request.onerror = (err) => {
        reject(err);
      };
    });
  }

  //#endregion

  //#region Update

  async update(
    dbName: string,
    tableName: string,
    data: any,
    preClean?: { index: string },
  ): Promise<string> {
    if (preClean?.index) {
      await this.deleteByIndex(dbName, tableName, preClean.index);
    }
    const result = await this._update(dbName, tableName, data);
    return result as string;
  }

  private _update(dbName: string, tableName: string, data: any) {
    return new Promise((resolve, reject) => {
      Array.isArray(data) || (data = [data]);
      const tx = this.databases
        .get(dbName)!
        .transaction(tableName, 'readwrite');
      let table = tx.objectStore(tableName);

      data.forEach((item: any) => {
        let request;
        if (!item.isDeleted && !item.markedForDeletion) {
          request = table.put(item);
        } else {
          this._delete(dbName, tableName, item);
        }
      });

      tx.oncomplete = () => {
        resolve(data);
      };

      tx.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  async updatePartial(
    dbName: string,
    tableName: string,
    data: any,
    filter?: { index: string; value: Array<string | number> },
    preClean?: { index: string },
  ): Promise<string> {
    if (preClean?.index) {
      await this.deleteByIndex(dbName, tableName, preClean.index);
    }
    const result = await this._updatePartial(dbName, tableName, data, filter);
    return result as string;
  }

  private _updatePartial(
    dbName: string,
    tableName: string,
    data: any,
    filter?: { index: string; value: Array<string | number> },
  ) {
    return new Promise((resolve, reject) => {
      const tx = this.databases
        .get(dbName)!
        .transaction(tableName, 'readwrite');
      let table = tx.objectStore(tableName);
      const dataPool = filter ? table.index(filter.index) : table;
      const cursor = filter
        ? dataPool.openKeyCursor(filter?.value)
        : dataPool.openCursor();

      cursor.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const currentValue = cursor.value;
          const newValue = { ...currentValue, ...data };
          cursor.update(newValue);
          cursor.continue();
        } else {
          resolve(data);
        }
      };

      tx.oncomplete = () => {
        resolve(data);
      };

      tx.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  //#endregion

  //#region Delete

  async delete(
    dbName: string,
    tableName: string,
    id?: number | string,
  ): Promise<string> {
    let data;
    let result;

    if (id) {
      let data = await this.get(dbName, tableName, id!);
      result = await this._delete(dbName, tableName, data);
    } else {
      result = await this._deleteAll(dbName, tableName);
    }

    return result as string;
  }

  async deleteByIndex(
    dbName: string,
    tableName: string,
    index: string,
  ): Promise<string> {
    let result = await this._deleteByIndex(dbName, tableName, index);
    return result as string;
  }

  private _delete(dbName: string, tableName: string, data: any) {
    return new Promise((resolve, reject) => {
      const tx = this.databases
        .get(dbName)!
        .transaction(tableName, 'readwrite');
      let table = tx.objectStore(tableName);
      let deleteKey = Array.isArray(table.keyPath)
        ? table.keyPath.map((key) => data[key])
        : data[table.keyPath!];

      let request = table.delete(deleteKey);

      request.onsuccess = () => {
        resolve(data);
      };

      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  private _deleteByIndex(dbName: string, tableName: string, index: string) {
    return new Promise((resolve, reject) => {
      const tx = this.databases
        .get(dbName)!
        .transaction(tableName, 'readwrite');
      let table = tx.objectStore(tableName);
      let indexStore = table.index(index);
      let request = indexStore.openCursor();
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve(undefined);
        }
      };
      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  private _deleteAll(dbName: string, tableName: string) {
    return new Promise((resolve, reject) => {
      const tx = this.databases
        .get(dbName)!
        .transaction(tableName, 'readwrite');
      let table = tx.objectStore(tableName);

      let request = table.clear();

      request.onsuccess = () => {
        resolve(undefined);
      };

      request.onerror = (event: Event) => {
        reject(event);
      };
    });
  }

  //#endregion
}
